import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ProfileView } from '../../shared/api/types';
import { Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { LegacyBadges } from '../../shared/ui/LegacyBadges';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { Avatar } from '../../shared/ui/Avatar';
import { Profile360Card } from '../profile/Profile360Card';
import { whatsappTo } from '../../shared/whatsapp';
import { useChurchCover } from '../profile/useChurchCover';

const ROLE_LABELS: Record<string, string> = { pastor: 'Pastor', admin: 'Administrador', department_leader: 'Líder', member: 'Miembro', guest: 'Invitado' };
const ORDER = ['pastor', 'department_leader', 'admin', 'member', 'guest'];

/** «Pastor · Miembro»: solo los roles conocidos, en orden de importancia. */
export function rolesLine(roles: string[] | undefined): string {
  return ORDER.filter((r) => roles?.includes(r)).map((r) => ROLE_LABELS[r]).join(' · ');
}

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** Perfil de una persona de mi iglesia, con lo que ella decidió mostrar. */
export function PersonProfilePage() {
  const me = useMe();
  const { personId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const churchId = me.activeChurchId;
  const profile = useAsync(
    (signal) => (churchId ? api.get<ProfileView>(`/multichurch/churches/${churchId}/people/${personId}/profile`, signal) : Promise.resolve(null)),
    [churchId, personId],
  );
  const church = useChurchCover(churchId);
  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  if (profile.status === 'loading') return <div className="page"><Loading /></div>;
  if (profile.status === 'error') return <div className="page"><ErrorState error={profile.error} onRetry={profile.reload} /></div>;
  const p = profile.data;
  if (!p) return null;

  const hasLinks = p.family && p.family.families.length > 0;
  const cover = church.status === 'ready' && church.data ? church.data.logoUrl : null;
  const hasDetails = p.phone || p.homePhone || p.email || p.birthday || p.address;
  return (
    <div className="page profile">
      <p className="small">
        {/* Si se llegó directo (sin historial en la app), regresa al inicio. */}
        <button type="button" className="link-button" onClick={() => (location.key === 'default' ? navigate('/app') : navigate(-1))}>
          ← Regresar
        </button>
        {me.can('members.read') ? (
          <>
            {' · '}
            <Link to={`/app/miembros/${p.personId}`}>Ver ficha de miembro</Link>
          </>
        ) : null}
      </p>
      <header className="wall-head">
        <div className="wall-cover" style={cover ? { backgroundImage: `url(${cover})` } : undefined} aria-hidden="true" />
        <div className="wall-head__main">
          <span className="profile-head__photo">
            <Avatar personId={p.personId} name={p.name} size={112} />
          </span>
          <div className="wall-head__name">
            <h1>
              {p.name}
              <LegacyBadges founder={p.founder} />
            </h1>
            {rolesLine(p.roles) ? <p className="wall-head__tagline">{rolesLine(p.roles)}</p> : null}
            {me.activeContext ? <p className="small muted">{me.activeContext.churchName}</p> : null}
            {p.isMe ? (
              <p className="small muted">
                Así ves tu perfil completo. Los demás ven solo lo que elegiste en <Link to="/app/perfil">Mi perfil</Link>.
              </p>
            ) : null}
          </div>
          <div className="wall-head__actions">
            {p.phone ? (
              <a className="btn btn--primary" href={whatsappTo(p.phone)} target="_blank" rel="noopener noreferrer">
                Mensaje por WhatsApp
              </a>
            ) : null}
            {p.phone ? (
              <a className="btn btn--secondary" href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>
                Llamar
              </a>
            ) : null}
            {p.isMe ? (
              <Link className="btn btn--secondary" to="/app/perfil">
                Editar mi perfil
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      <div className="wall-body">
        <aside className="wall-side" aria-label="Datos de la persona">
          {hasDetails ? (
            <Card title="Datos">
              <dl className="details">
                {p.phone ? (
                  <>
                    <dt>Celular</dt>
                    <dd>
                      <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>{p.phone}</a>
                    </dd>
                  </>
                ) : null}
                {p.homePhone ? (
                  <>
                    <dt>Teléfono de casa</dt>
                    <dd>
                      <a href={`tel:${p.homePhone.replace(/[^\d+]/g, '')}`}>{p.homePhone}</a>
                    </dd>
                  </>
                ) : null}
                {p.email ? (
                  <>
                    <dt>Correo</dt>
                    <dd>
                      <a href={`mailto:${p.email}`}>{p.email}</a>
                    </dd>
                  </>
                ) : null}
                {p.birthday ? (
                  <>
                    <dt>Cumpleaños</dt>
                    <dd>
                      {p.birthday.day} de {MONTHS[p.birthday.month - 1]}
                    </dd>
                  </>
                ) : null}
                {p.address ? (
                  <>
                    <dt>Dirección</dt>
                    <dd>{p.address}</dd>
                  </>
                ) : null}
              </dl>
            </Card>
          ) : null}

          {hasLinks ? (
            <Card title="Familia">
              <p>{p.family!.families.join(' · ')}</p>
            </Card>
          ) : null}
        </aside>

        <div className="wall-feed">
          {p.bio ? (
            <Card title="Acerca de mí">
              <p className="pre-line">{p.bio}</p>
            </Card>
          ) : null}

          {p.ministries && p.ministries.length > 0 ? (
            <Card title="Ministerios">
              <ul className="ministry-tags">
                {p.ministries.map((m) => (
                  <li key={m.id}>
                    <Link to={`/app/ministerios/${m.id}`}>{m.name}</Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {!p.bio && !hasDetails && !(p.ministries && p.ministries.length) && !hasLinks ? (
            <EmptyState title="Esta persona todavía no comparte más datos." />
          ) : null}
          {!p.isMe ? <Profile360Card churchId={churchId} personId={p.personId} /> : null}
        </div>
      </div>
    </div>
  );
}
